import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import { Slot } from '@radix-ui/react-slot';
import { useTranslation } from 'react-i18next';

import { Button } from '@app/shared/ui/atoms/Common';
import { Popover, PopoverContent, PopoverTrigger } from '@app/shared/ui/molecules/Common';
import { useMultiScrollLock } from '@app/shared/hooks/useMultipleScrollLock';
import { pandaHelloImage } from '@app/assets';
import { cn } from '@app/shared/utils/common';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { useDailyRewardModal } from '@app/shared/hooks/useDailyRewardModal';
import { useGetCurrentUserProfileQuery } from '@/app/store/slices/users/usersApi';

interface TutorialProps {
  text: string;
  asChild?: boolean;
  scrollableElementRefs?: React.RefObject<HTMLElement | null>[];
  children: React.ReactNode;
  enabled?: boolean;
  shouldPulse?: boolean;
}

export function Tutorial({
  text,
  asChild = false,
  scrollableElementRefs = [],
  children,
  enabled,
  shouldPulse = true,
}: TutorialProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((state) => state.tutorial);

  const containerRef = useRef<HTMLDivElement>(null);

  const { data: userProfile, isLoading: isLoadingProfile } = useGetCurrentUserProfileQuery();
  const { dailyRewardStatus, isPredictionModalOpen } = useDailyRewardModal();

  useMultiScrollLock({ refs: scrollableElementRefs, enabled: true, includeBody: true });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const Comp = asChild ? Slot : 'div';

  const getCurrentStep = () => {
    if (tutorial.step === 'HOME_STONE_1') {
      return 1;
    }
    if (tutorial.step === 'HOME_SETTLEMENT_1') {
      return 2;
    }
    if (tutorial.step === 'HOME_CALC_1') {
      return 3;
    }
    if (tutorial.step === 'CALC_1') {
      return 4;
    }
    if (tutorial.step === 'CALC_2') {
      return 5;
    }
    return 0;
  };

  if (
    !enabled ||
    isLoadingProfile ||
    !userProfile?.isUserAgreementRead ||
    !dailyRewardStatus?.claimedToday ||
    isPredictionModalOpen
  ) {
    return <>{children}</>;
  }

  return (
    <Popover open>
      <PopoverTrigger
        className={cn(
          'relative z-101 pointer-events-auto',
          shouldPulse && '[&_img]:brightness-150'
        )}
        asChild
      >
        <Comp ref={containerRef}>{children}</Comp>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={50}
        withOverlay
        className="min-h-[100px] w-fit max-w-[320px] rounded-2xl bg-blue-light p-0"
      >
        <div className={'flex relative items-end gap-3  p-0 pr-6 pl-3'}>
          <div
            onClick={() => dispatch(tutorialActions.skip())}
            className="absolute z-1 right-1 top-1 h-6 w-6 rounded-full bg-[#0006] opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="m-auto h-6 w-6" color="white" />
            <span className="sr-only">Close</span>
          </div>
          <img src={pandaHelloImage} alt="panda-hello" className="h-[100px]" />
          <TypeAnimation
            sequence={[text]}
            wrapper="p"
            speed={{ type: 'keyStrokeDelayInMs', value: 25 }}
            cursor={false}
            className="self-center py-2 text-[13px] font-medium leading-tight text-[#2c5282] opacity-70"
          />
        </div>
        {!!getCurrentStep() && (
          <div className={'flex gap-2 justify-end items-center px-1 pb-1'}>
            {getCurrentStep() === 1 ? (
              <div className={'w-6 h-6'} />
            ) : (
              <Button
                onClick={() => dispatch(tutorialActions.prevStep())}
                size={'sm'}
                className={'w-fit px-2'}
                variant={'secondary'}
              >
                {t(`common.back`)}
              </Button>
            )}
            <span>{getCurrentStep()}/5</span>
            <Button
              onClick={() => {
                if (getCurrentStep() === 5) {
                  dispatch(tutorialActions.skip());
                } else {
                  dispatch(tutorialActions.nextStep());
                }
              }}
              size={'sm'}
              className={'w-fit px-2 animate-pulse'}
            >
              {getCurrentStep() === 5 ? t(`common.finish`) : t(`common.next`)}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
